import { createAction, createPayloadAction } from 'helpers/actionsHelpers'
import { PayloadAction, SimpleAction, CompositeAction } from 'interfaces/action'
import { Timeframe } from 'interfaces/chart'
import { TrendRankMode } from 'interfaces/rank'
import { CategoryId } from 'interfaces/meta'
import { CategorizedPageType, HighlightCategoryPageType, TimeframedPage } from 'interfaces/pages'
import { ErrorState, UnexpectedErrorType } from 'interfaces/errors'
import { constructError } from 'helpers/errorHelpers'
import { createRequestAction } from 'helpers/actionHelpers/createRequestAction'
import { FeatureFlags } from 'interfaces/global'

type SetTimeframeActionPayload = {
  type: TimeframedPage
  timeframe: Timeframe
}

type SetFilterCategoryActionPayload = {
  type: CategorizedPageType
  category: CategoryId
}

type SetUnexpectedErrorPayload = ErrorState & {
  silent?: boolean
}

export const RESET_STATE = 'RESET_STATE' as const
type ResetState = PayloadAction<typeof RESET_STATE, (() => CompositeAction[]) | null>
// will dispatch actions returned by actionsCreator after reset
export const resetStateActionCreator = (actionsCreator: (() => CompositeAction[]) | null = null): ResetState =>
  createPayloadAction(RESET_STATE, actionsCreator)

export const RESET_FINANCIAL_STATE = 'RESET_FINANCIAL_STATE' as const
export const resetFinancialStateActionCreator = (): SimpleAction<typeof RESET_FINANCIAL_STATE> => createAction(RESET_FINANCIAL_STATE)
export type ResetFinancialState = ReturnType<typeof resetFinancialStateActionCreator>

export const SET_TIMEFRAME = 'SET_TIMEFRAME' as const
type SetTimeframe = PayloadAction<typeof SET_TIMEFRAME, SetTimeframeActionPayload>
export const setTimeframeActionCreator = (type: TimeframedPage, timeframe: Timeframe): SetTimeframe =>
  createPayloadAction(SET_TIMEFRAME, { type, timeframe })

export const SET_FILTER_CATEGORY = 'SET_FILTER_CATEGORY' as const
type SetFilterCategoryAction = PayloadAction<typeof SET_FILTER_CATEGORY, SetFilterCategoryActionPayload>
export const setFilterCategoryActionCreator = (type: CategorizedPageType, category: CategoryId) =>
  createPayloadAction(SET_FILTER_CATEGORY, { type, category })

export const HIGHLIGHT_CATEGORY = 'HIGHLIGHT_CATEGORY' as const
type HighlightCategory = PayloadAction<typeof HIGHLIGHT_CATEGORY, { type: HighlightCategoryPageType, category: CategoryId | null }>
export const highlightCategoryActionCreator = (type: HighlightCategoryPageType, category: CategoryId | null): HighlightCategory =>
  createPayloadAction(HIGHLIGHT_CATEGORY, { type, category })

export const SET_TREND_MODE = 'SET_TREND_MODE' as const
type SetTrendModeAction = PayloadAction<typeof SET_TREND_MODE, { type: TimeframedPage, mode: TrendRankMode }>
export const setTrendModeActionCreator = (type: TimeframedPage, mode: TrendRankMode): SetTrendModeAction =>
  createPayloadAction(SET_TREND_MODE, { type, mode })

export type PageChangedPayload = {
  prev: string | null
  next: string
  action: 'push' | 'pop' | 'initial' | 'replace'
}

export const PAGE_CHANGED = 'PAGE_CHANGED' as const
type PageChanged = PayloadAction<typeof PAGE_CHANGED, PageChangedPayload>
export const pageChangedActionCreator = (payload: PageChangedPayload): PageChanged =>
  createPayloadAction(PAGE_CHANGED, payload)

export const SET_UNEXPECTED_ERROR = 'SET_UNEXPECTED_ERROR' as const
type SetUnexpectedError = PayloadAction<typeof SET_UNEXPECTED_ERROR, SetUnexpectedErrorPayload>
export const setUnexpectedErrorActionCreator = (errorType: UnexpectedErrorType, internalError?: Error, silent?: boolean): SetUnexpectedError =>
  createPayloadAction(SET_UNEXPECTED_ERROR, { ...constructError(errorType, internalError), silent }) as SetUnexpectedError

export const HANDLE_NETWORK_ERROR = 'HANDLE_NETWORK_ERROR' as const
type HandleNetworkError = SimpleAction<typeof HANDLE_NETWORK_ERROR>
export const handleNetworkErrorActionCreator = (): HandleNetworkError =>
  createAction(HANDLE_NETWORK_ERROR)

export const HANDLE_UNAUTHORIZED_ERROR = 'HANDLE_UNAUTHORIZED_ERROR' as const
type HandleUnauthorizedError = SimpleAction<typeof HANDLE_UNAUTHORIZED_ERROR>
export const handleUnauthorizedErrorActionCreator = (): HandleUnauthorizedError =>
  createAction(HANDLE_UNAUTHORIZED_ERROR)

export const MARK_DATA_STALE = 'MARK_DATA_STALE' as const
type MarkDataStale = PayloadAction<typeof MARK_DATA_STALE, string>
export const markDataStaleActionCreator = (dataPieceName: string) =>
  createPayloadAction(MARK_DATA_STALE, dataPieceName)

export class GetFeatureFlagsAction extends createRequestAction<'GET_FEATURE_FLAGS', void, FeatureFlags>('GET_FEATURE_FLAGS') {}

export type GlobalAction =
  | SetTimeframe
  | SetTrendModeAction
  | SetFilterCategoryAction
  | HighlightCategory
  | PageChanged
  | ResetState
  | SetUnexpectedError
  | HandleUnauthorizedError
  | HandleNetworkError
  | MarkDataStale
  | ResetFinancialState
  | typeof GetFeatureFlagsAction.Type

export default GlobalAction
